import { editorReduxStore } from "./redux/store";
import { selectionEquals } from "./selection/equals";
import { selectionEntityId } from "./selection/entity-id";
import {
  didEmailSettingsChange,
  findHistoryRevealBlockId,
  selectionForBlockId,
} from "./history-reveal";
import type { EmailDocument, Selection } from "./types";

const LABEL_MARGIN_PX = 24;
const PULSE_MS = 600;

type RevealOptions = { scroll?: boolean; pulse?: boolean };

function isFullyVisible(container: HTMLElement, target: HTMLElement): boolean {
  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  return (
    targetRect.top >= containerRect.top + LABEL_MARGIN_PX &&
    targetRect.bottom <= containerRect.bottom - LABEL_MARGIN_PX
  );
}

function scrollIntoViewWithMargin(
  container: HTMLElement,
  target: HTMLElement,
  marginPx: number,
): void {
  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();

  const overflowTop = containerRect.top + marginPx - targetRect.top;
  const overflowBottom = targetRect.bottom - (containerRect.bottom - marginPx);

  if (overflowTop > 0) {
    container.scrollTop -= overflowTop;
  } else if (overflowBottom > 0) {
    container.scrollTop += overflowBottom;
  }
}

function pulseElement(target: HTMLElement): void {
  if (
    globalThis.window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    return;
  }

  target.removeAttribute("data-pulsing");
  void target.offsetWidth;
  target.setAttribute("data-pulsing", "true");
  globalThis.window.setTimeout(() => {
    target.removeAttribute("data-pulsing");
  }, PULSE_MS);
}

export function revealSelection(
  selection: Selection,
  options: RevealOptions = {},
): void {
  const scroll = options.scroll ?? true;
  const pulse = options.pulse ?? true;
  const entityId = selectionEntityId(selection);
  if (!entityId) {
    return;
  }

  const scrollRoot = globalThis.document.getElementById("main-canvas");
  if (!scrollRoot) {
    return;
  }

  const target = scrollRoot.querySelector(`[data-entity-id="${entityId}"]`);
  if (!(target instanceof HTMLElement)) {
    return;
  }

  if (scroll && !isFullyVisible(scrollRoot, target)) {
    scrollIntoViewWithMargin(scrollRoot, target, LABEL_MARGIN_PX);
  }

  if (pulse) {
    pulseElement(target);
  }
}

let revealFrame = 0;

function scheduleReveal(run: () => void): void {
  if (revealFrame) {
    globalThis.cancelAnimationFrame(revealFrame);
  }
  revealFrame = globalThis.requestAnimationFrame(() => {
    revealFrame = globalThis.requestAnimationFrame(() => {
      revealFrame = 0;
      run();
    });
  });
}

export function scheduleFocusAndRevealSelection(
  selection: Selection,
  options?: RevealOptions,
): void {
  scheduleReveal(() => {
    focusAndRevealSelection(selection, options);
  });
}

export function scheduleRevealSelection(): void {
  scheduleReveal(() => {
    const { mode, selection } = editorReduxStore.getState().editor;
    if (mode !== "design") {
      return;
    }
    revealSelection(selection);
  });
}

export function scheduleHistoryReveal(
  beforeDocument: EmailDocument,
  afterDocument: EmailDocument,
  selection: Selection,
): void {
  scheduleReveal(() => {
    const { mode } = editorReduxStore.getState().editor;
    if (mode !== "design") {
      return;
    }

    const preferredBlockId = selection.kind === "block" ? selection.blockId : null;
    const diffBlockId = findHistoryRevealBlockId(
      beforeDocument,
      afterDocument,
      preferredBlockId,
    );
    const diffSelection =
      diffBlockId === null
        ? null
        : selectionForBlockId(afterDocument, diffBlockId);

    const targets: Selection[] = [];
    if (selectionEntityId(selection) !== null) {
      targets.push(selection);
    }

    if (
      diffSelection !== null &&
      !targets.some(target => selectionEquals(target, diffSelection))
    ) {
      targets.push(diffSelection);
    }

    if (targets.length === 0 && didEmailSettingsChange(beforeDocument, afterDocument)) {
      return;
    }

    for (const target of targets) {
      revealSelection(target);
    }
  });
}

export function blurPanelField(): void {
  const active = globalThis.document.activeElement;
  if (
    active instanceof HTMLInputElement ||
    active instanceof HTMLTextAreaElement ||
    active instanceof HTMLSelectElement
  ) {
    active.blur();
  }
}

export function blurCanvasFocus(): void {
  const blurFocusedInCanvas = () => {
    const canvas = globalThis.document.getElementById("main-canvas");
    const focused = globalThis.document.activeElement;
    if (focused instanceof HTMLElement && canvas?.contains(focused)) {
      focused.blur();
    }
  };

  blurFocusedInCanvas();
  globalThis.requestAnimationFrame(() => {
    blurFocusedInCanvas();
  });
}

export function focusSelectionFrame(selection: Selection): void {
  const entityId = selectionEntityId(selection);
  if (!entityId) {
    blurCanvasFocus();
    return;
  }

  const scrollRoot = globalThis.document.getElementById("main-canvas");
  if (!scrollRoot) {
    return;
  }

  const target = scrollRoot.querySelector(`[data-entity-id="${entityId}"]`);
  if (target instanceof HTMLElement) {
    target.focus({ preventScroll: true });
  }
}

export function focusAndRevealSelection(
  selection: Selection,
  options?: RevealOptions,
): void {
  revealSelection(selection, options);
  focusSelectionFrame(selection);
}
